import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Error, TurnedInNotOutlined, HowToReg } from '@mui/icons-material';
import { ArrowBackIos } from '@mui/icons-material/';
import { Box, Stack, TextField, MenuItem } from '@mui/material';
import { Input, Toast } from '../../components';
import DeleteIcon from 'app/components/Svgs/DeleteIcon';
import { Alert, Loader, StyledButton } from 'app/components/v2';
import {
  FormContainer,
  ChildGrid,
  SubTitle,
  InputContainerStack,
  SubTitleText,
  SubConatiner3,
  ParentGrid,
  CustomIconButton,
  ParentPaper,
  Text,
  IconTextStack,
  SubContainer,
  Container,
  CustomAdornment,
} from './style';
import { setAdduser } from 'app/store/features/addUserSlice';
import { setRoute } from 'app/store/features/previousRouteSlice';
import { setAlertOpen } from 'app/store/features/toggleSidebarSlice';
import { ColorsV2 } from 'app/constants/color';
import apiService from 'app/services/apiService';
import { useFormik } from 'formik';
import * as yup from 'yup';

// eslint-disable-next-line complexity
const Adduser = () => {
  const [roles, setRoles] = useState([]);
  const [authorities, setAuthorities] = useState([]);
  const [open, setOpen] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [del, setDel] = useState(false);
  const [loader, setLoader] = useState(false);
  const { darkmode } = useSelector(state => state.dark);

  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const accountSettings = useSelector(state => state.updateUser.userData);
  const userData = useSelector(state => state.addUser);

  const route = useSelector(state => state.previousRoute.route);

  const state = location.state;
  const pathname = location.pathname;
  const data = state?.id;
  const title = state?.title;
  let flag = '';
  if (flag === '') {
    flag = state?.addUser;
  }

  const { t } = useTranslation('translation', { keyPrefix: 'addUser_headings' });

  const phoneNumberAutoFormat = phoneNumber => {
    const number = phoneNumber.trim().replace(/[^{+}0-9]/g, '');

    if (number.length < 8) return number;
    return number.replace(/(\d{11})(\d{10})/, '$1$2');
  };

  const onChange = e => {
    const targetValue = phoneNumberAutoFormat(e.target.value);
    return targetValue;
  };

  const fetchRoles = async () => {
    const res = await apiService.getRoles();
    setRoles(res.data);
  };

  const fetchAuthorities = async () => {
    const res = await apiService.getAuthorities();
    setAuthorities(res.data);
  };
  useEffect(() => {
    fetchRoles();
    fetchAuthorities();
    dispatch(setRoute({ pathname }));
  }, []);

  useEffect(() => {
    if(!Object.keys(authorities).length || !Object.keys(accountSettings).length) return;
    const isAdmin = authorities.find(
      (auth) => auth.id === accountSettings.auth && auth.level >= 100
    );
    // Prevent editing your own user, this should be done through the settings page
    const selfEdit = accountSettings.id === userData.id ? true : false;
    if (isAdmin && !selfEdit) {
      // Check if the user is an admin and not editing their own profile
      // This block is kept intentionally empty to maintain consistency with other admin checks
    } else {
      navigate('/users', {
        state: {
          message: 'Unauthorized',
          severity: 'error',
        },
      });
    }
  }, [accountSettings, authorities]);

  const handleDelete = () => {
    setDel(true);
    setOpen(true);
  };

  const handleRouteNavigationConfirmation = () => {
    navigate('/users');
    dispatch(setRoute({ pathname: '/users' }));
  };

  const handleDeleteConfimation = async () => {
    try {
      const res = await apiService.deleteUser(title === 'Edit' ? data.id : userData.id);
      if (res.data.status === 'success') {
        navigate('/users', {
          state: {
            message: 'User Deleted Successfully',
            severity: 'success',
          },
        });
      }
    } catch (error) {
      console.error(error.response);
      navigate(`/edit-user/${userData.id}`, {
        state: {
          message: error.response.statusText,
          severity: 'error',
        },
      });
    }
  };

  const addUserSchema = yup.object().shape({
    firstName: yup.string().required(t('error_fname')),
    lastName: yup.string().required(t('error_lname')),
    email: yup.string().email(t('email_invalid')).required(t('error_email')),
    phoneNr: yup.string().min(9).required(t('error_phoneNr')),
    persNr: yup.string().min(12).required(t('error_persNr')),
    role: yup.string().required(t('error_role')),
    auth: yup.string().required(t('error_auth')),
  });

  const { values, errors, handleChange, handleBlur, handleSubmit, setFieldValue, touched } =
    useFormik({
      initialValues: {
        firstName: title === 'Edit' ? data.firstName.trim() : flag ? '' : userData.firstName.trim(),
        lastName: title === 'Edit' ? data.lastName.trim() : flag ? '' : userData.lastName.trim(),
        email: title === 'Edit' ? data.email.trim() : flag ? '' : userData.email.trim(),
        phoneNr: title === 'Edit' ? data.phoneNr : flag ? '' : userData.phoneNr,
        id: title === 'Edit' ? data.id : flag ? '' : userData.id,
        persNr: title === 'Edit' ? data.persNr : flag ? '' : userData.persNr,
        role: title === 'Edit' ? data.role : flag ? '' : userData.role,
        auth: title === 'Edit' ? data.auth : flag ? '' : userData.auth,
        orgId: 'db7b126c-4c26-47e6-a061-43855237c20b',
      },
      validationSchema: addUserSchema,
      onSubmit(values, { resetForm }) {
        const user = {
          users: [
            {
              ...values,
            },
          ],
        };

        setLoader(true);
        if (title === 'Edit' || route.includes('edit-user')) {
          apiService
            .updateUser(user)
            .then(resp => {
              if (resp.status === 'success') {
                resetForm({ values: '' });
                setLoader(false);
                navigate('/users', {
                  state: {
                    message: 'User Updated Successfully',
                    severity: 'success',
                  },
                });
                dispatch(setAlertOpen(false));
                dispatch(setRoute({ pathname: '/users' }));
              }
            })
            .catch(error => {
              console.error(error);
              setLoader(false);
              setToastOpen(true);
              navigate(`/edit-user/${userData.id}`, {
                state: {
                  message: error.response.statusText,
                  severity: 'error',
                },
              });
              dispatch(setRoute({ pathname: '/users' }));
            });
        } else {
          setAdduser(values);
          apiService
            .setUser(user)
            .then(resp => {
              if (resp.status === 'success') {
                resetForm({ values: '' });
                setLoader(false);
                navigate('/users', {
                  state: {
                    message: 'User Added Successfully',
                    severity: 'success',
                  },
                });
                dispatch(setAlertOpen(false));
                dispatch(setRoute({ pathname: '/users' }));
              }
            })
            .catch(error => {
              console.error(error);
              setLoader(false);
              setToastOpen(true);
              navigate('/add-user', {
                state: {
                  message: error.response.statusText,
                  severity: 'error',
                },
              });
              dispatch(setRoute({ pathname: '/users' }));
            });
        }
      },
    });

  const handleDisable = () => {
    if (
      userData?.firstName === values.firstName &&
      userData?.lastName === values.lastName &&
      userData?.email === values.email &&
      userData?.phoneNr === values.phoneNr &&
      userData?.role === values.role &&
      userData?.auth === values.auth
    ) {
      return true;
    }
    return false;
  };

  const handleToggleAlert = () => {
    setDel(false);
    if (
      !values.firstName &&
      !values.lastName &&
      !values.email &&
      !values.persNr &&
      !values.phoneNr &&
      !values.role &&
      !values.auth
    ) {
      navigate('/users');
      dispatch(setRoute({ pathname: '/users' }));
    } else if (
      userData?.firstName === values.firstName &&
      userData?.lastName === values.lastName &&
      userData?.email === values.email &&
      userData?.phoneNr === values.phoneNr &&
      userData?.role === values.role &&
      userData?.auth === values.auth
    ) {
      dispatch(setRoute({ pathname: '/users' }));
      navigate('/users');
    } else {
      setOpen(true);
    }
  };
  useEffect(() => {
    if (
      !values.firstName &&
      !values.lastName &&
      !values.email &&
      !values.persNr &&
      !values.phoneNr &&
      !values.role &&
      !values.auth
    ) {
      dispatch(setAlertOpen(false));

      dispatch(setRoute({ pathname: '/users' }));
    } else if (
      userData?.firstName === values.firstName &&
      userData?.lastName === values.lastName &&
      userData?.email === values.email &&
      userData?.phoneNr === values.phoneNr &&
      userData?.role === values.role &&
      userData?.auth === values.auth
    ) {
      dispatch(setAlertOpen(false));
    } else {
      dispatch(setAlertOpen(true));
    }
  }, [values]);
  return (
    <Container sx={{ backgroundColor: ColorsV2.primary }}>
      <SubContainer paddingBottom={1}>
        <IconTextStack direction='row' spacing={3}>
          <CustomIconButton onClick={handleToggleAlert}>
            <ArrowBackIos
              sx={{
                height: '15px',
                width: '15px',
                color: ColorsV2.white,
              }}
            />
          </CustomIconButton>
          <Text>{route.includes('edit-user') ? t('edit') : t('main')}</Text>
        </IconTextStack>
        <Box>
          <StyledButton
            name='IconButton'
            onClick={handleSubmit}
            disabled={handleDisable()}
            startIcon={
              route.includes('edit-user') ? (
                <HowToReg sx={{ fontSize: '0.6em' }} />
              ) : (
                <TurnedInNotOutlined sx={{ fontSize: '0.6em' }} />
              )
            }
            sx={{ fontSize: '0.7em', fontWeight: 'light' }}
          >
            {route.includes('edit-user') ? t('update') : t('save')}
          </StyledButton>
        </Box>
      </SubContainer>

      {!loader && (
        <ParentPaper
          elevation={0}
          sx={{
            backgroundColor: ColorsV2.primary,
            border: 0,
            borderRadius: '0px',
          }}
        >
          <ParentGrid container>
            <ChildGrid item sm={5.5}>
              <SubConatiner3>
                <SubTitle gutterBottom sx={{ color: ColorsV2.white }}>
                  {t('sub_1')}
                </SubTitle>
                <SubTitleText sx={{ color: ColorsV2.white }}>{t('desc_1')}</SubTitleText>
              </SubConatiner3>
              <FormContainer>
                <form onSubmit={handleSubmit}>
                  <Stack spacing={1.5}>
                    <div style={{ display: 'flex' }}>
                      <Input
                        // label={t('fname')}
                        variant='outlined'
                        size='small'
                        val={values.firstName}
                        name='firstName'
                        touched={touched.firstName}
                        inperror={errors.firstName}
                        handlechange={handleChange}
                        handleblur={handleBlur}
                        InputProps={{
                          startAdornment: (
                            <CustomAdornment position='start' sx={{ color: ColorsV2.white }}>
                              {t('fname')}:
                            </CustomAdornment>
                          ),
                          style: {
                            color: ColorsV2.white,
                            fontFamily: 'DM Sans, sans-serif',
                            fontSize: '0.7em',
                            fontWeight: '400',
                            lineHeight: '21px',
                            borderRadius: '8px',
                            height: '2.7em',
                          },
                        }}
                        InputLabelProps={{
                          style: {
                            color: darkmode || errors.firstName ? ColorsV2.white : 'inherit',
                          },
                        }}
                        sx={{
                          width: '49%',
                          backgroundColor: 'transparent',

                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: ColorsV2.white,
                          },
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: ColorsV2.white,
                            },
                          },

                          marginRight: '10px',
                          '& label.Mui-focused': {
                            color: ColorsV2.white,
                            transform: 'translate(15px, -16px)',
                            fontSize: '0.75rem',
                          },
                          '&  .MuiFormHelperText-root.Mui-error': {
                            background: ColorsV2.secondary,
                            color: '#FF2800',
                            width: '101%',
                            height: '100%',
                            fontSize: '1rem',
                            marginTop: '-0.5%',
                            marginLeft: -0.15,
                          },
                        }}
                      />

                      <Input
                        // label={t('lname')}
                        variant='outlined'
                        val={values.lastName}
                        size='small'
                        name='lastName'
                        touched={touched.lastName}
                        inperror={errors.lastName}
                        handlechange={handleChange}
                        handleblur={handleBlur}
                        InputProps={{
                          startAdornment: (
                            <CustomAdornment position='start' sx={{ color: ColorsV2.white }}>
                              {t('lname')}:
                            </CustomAdornment>
                          ),
                          style: {
                            color: ColorsV2.white,
                            fontFamily: 'DM Sans, sans-serif',
                            fontSize: '0.7em',
                            fontWeight: '400',
                            lineHeight: '21px',
                            borderRadius: '8px',
                            height: '2.7em',
                          },
                        }}
                        InputLabelProps={{
                          style: {
                            color: darkmode || errors.firstName ? ColorsV2.white : 'inherit',
                          },
                        }}
                        sx={{
                          width: '49%',
                          backgroundColor: 'transparent',

                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: ColorsV2.white,
                          },
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: ColorsV2.white,
                            },
                          },

                          '& label.Mui-focused': {
                            color: ColorsV2.white,
                            transform: 'translate(15px, -16px)',
                            fontSize: '0.75rem',
                          },
                          '&  .MuiFormHelperText-root.Mui-error': {
                            background: ColorsV2.secondary,
                            color: '#FF2800',
                            width: '101%',
                            height: '100%',
                            fontSize: '1rem',
                            marginTop: '-0.5%',
                            marginLeft: -0.15,
                          },
                        }}
                      />
                    </div>

                    <Input
                      required
                      variant='outlined'
                      val={values.email}
                      size='small'
                      fullWidth
                      name='email'
                      touched={touched.email}
                      inperror={errors.email}
                      handlechange={handleChange}
                      handleblur={handleBlur}
                      InputProps={{
                        startAdornment: (
                          <CustomAdornment position='start' sx={{ color: ColorsV2.white }}>
                            {t('email')}:
                          </CustomAdornment>
                        ),
                        style: {
                          color: ColorsV2.white,
                          fontFamily: 'DM Sans, sans-serif',
                          fontSize: '0.7em',
                          fontWeight: '400',
                          lineHeight: '21px',
                          borderRadius: '8px',
                          height: '2.7em',
                        },
                      }}
                      InputLabelProps={{
                        style: {
                          color: darkmode || errors.firstName ? ColorsV2.white : 'inherit',
                        },
                      }}
                      sx={{
                        backgroundColor: 'transparent',

                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: ColorsV2.white,
                        },
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: ColorsV2.white,
                          },
                        },
                        // backgroundColor: '	'#778899',
                        '& label.Mui-focused': {
                          color: ColorsV2.white,
                          transform: 'translate(15px, -16px)',
                          fontSize: '0.75rem',
                        },
                        '&  .MuiFormHelperText-root.Mui-error': {
                          background: ColorsV2.secondary,
                          color: '#FF2800',
                          width: '101%',
                          height: '100%',
                          fontSize: '1rem',
                          marginTop: '-0.1%',
                          marginLeft: -0.15,
                        },
                      }}
                    />
                    <TextField
                      type='tel'
                      value={values.phoneNr}
                      size='small'
                      fullWidth
                      name='phoneNr'
                      error={touched.phoneNr && errors.phoneNr}
                      onBlur={handleBlur}
                      onChange={e => setFieldValue('phoneNr', onChange(e))}
                      helperText={
                        touched.phoneNr && errors.phoneNr ? (
                          <>
                            <Error sx={{ mr: 1, fontSize: '1rem', verticalAlign: 'middle' }} />
                            {errors.phoneNr}
                          </>
                        ) : null
                      }
                      InputProps={{
                        startAdornment: (
                          <CustomAdornment position='start' sx={{ color: ColorsV2.white }}>
                            {t('phone')}:
                          </CustomAdornment>
                        ),
                        style: {
                          color: ColorsV2.white,
                          fontFamily: 'DM Sans, sans-serif',
                          fontSize: '0.7em',
                          fontWeight: '400',
                          lineHeight: '21px',
                          borderRadius: '8px',
                          height: '2.7em',
                        },
                      }}
                      InputLabelProps={{
                        style: {
                          color: darkmode || errors.firstName ? ColorsV2.white : 'inherit',
                        },
                      }}
                      inputProps={{
                        maxLength: 14,
                        style: {
                          color: ColorsV2.white,
                        },
                      }}
                      sx={{
                        backgroundColor: 'transparent',

                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: ColorsV2.white,
                        },
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: ColorsV2.white,
                          },
                        },
                        // backgroundColor: '#778899',
                        '& label.Mui-focused': {
                          color: ColorsV2.white,
                          transform: 'translate(15px, -16px)',
                          fontSize: '0.75rem',
                        },
                        '&  .MuiFormHelperText-root.Mui-error': {
                          background: ColorsV2.secondary,
                          color: '#FF2800',
                          width: '101%',
                          height: '100%',
                          fontSize: '1rem',
                          marginTop: '-0.1%',
                          marginLeft: -0.15,
                        },
                        borderRadius: '8px',
                      }}
                    />
                    <TextField
                      type='tel'
                      value={values.persNr}
                      fullWidth
                      size='small'
                      disabled={route.includes('edit-user') && true}
                      name='persNr'
                      error={touched.persNr && errors.persNr}
                      onBlur={handleBlur}
                      onChange={e => setFieldValue('persNr', onChange(e))}
                      placeholder={t('placeholder')}
                      InputLabelProps={{
                        style: {
                          color: darkmode || errors.firstName ? ColorsV2.white : 'inherit',
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <CustomAdornment position='start' sx={{ color: ColorsV2.white }}>
                            {t('personal')}:
                          </CustomAdornment>
                        ),
                        style: {
                          color: ColorsV2.white,
                          fontFamily: 'DM Sans, sans-serif',
                          fontSize: '0.7em',
                          fontWeight: '400',
                          lineHeight: '21px',
                          borderRadius: '8px',
                          height: '2.7em',
                        },
                      }}
                      helperText={
                        touched.persNr && errors.persNr ? (
                          <>
                            <Error sx={{ mr: 1, fontSize: '1rem', verticalAlign: 'middle' }} />
                            {errors.persNr}
                          </>
                        ) : null
                      }
                      inputProps={{
                        maxLength: 12,
                        style: {
                          color: ColorsV2.white,
                        },
                      }}
                      sx={{
                        backgroundColor: 'transparent',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: ColorsV2.white,
                        },
                        '& .MuiInputBase-root.Mui-disabled': {
                          backgroundColor: '#92929D',
                          borderColor: ColorsV2.white,
                        },
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: 'white',
                          borderColor: ColorsV2.white,
                        },
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: ColorsV2.white,
                          },
                        },
                        // backgroundColor: '#778899',
                        '& label.Mui-focused': {
                          color: ColorsV2.white,
                          transform: 'translate(15px, -16px)',
                          fontSize: '0.75rem',
                        },
                        '&  .MuiFormHelperText-root.Mui-error': {
                          background: ColorsV2.secondary,
                          color: '#FF2800',
                          width: '101%',
                          height: '100%',
                          fontSize: '1rem',
                          marginTop: '-0.1%',
                          marginLeft: -0.15,
                        },

                        borderRadius: '8px',
                      }}
                    />
                  </Stack>
                </form>
              </FormContainer>
            </ChildGrid>
            <ChildGrid item sm={5.5}>
              <SubConatiner3>
                <SubTitle gutterBottom sx={{ color: ColorsV2.white }}>
                  {t('sub_2')}
                </SubTitle>
                <SubTitleText sx={{ color: ColorsV2.white }}>{t('desc_2')}</SubTitleText>
              </SubConatiner3>
              <InputContainerStack>
                <form onSubmit={handleSubmit}>
                  <Box
                    width='100%'
                    sx={{
                      position: 'relative',
                      // padding: '1px',
                    }}
                  >
                    <Input
                      select
                      fullWidth
                      size='small'
                      val={values.role}
                      InputProps={{
                        startAdornment: (
                          <CustomAdornment position='start' sx={{ color: ColorsV2.white }}>
                            {t('role')}:
                          </CustomAdornment>
                        ),
                        style: {
                          color: ColorsV2.white,
                          fontFamily: 'DM Sans, sans-serif',
                          fontSize: '0.7em',
                          fontWeight: '400',
                          lineHeight: '21px',
                          borderRadius: '8px',
                          height: '2.7em',
                        },
                      }}
                      inputProps={{
                        MenuProps: {
                          MenuListProps: {
                            sx: {
                              backgroundColor: ColorsV2.secondary,
                              color: 'white',
                              border: `2px solid ${ColorsV2.white}`,
                              '& .MuiMenuItem-root.Mui-selected': {
                                backgroundColor: ColorsV2.acent,
                                borderRadius: '8px',
                              },
                              '& .MuiMenuItem-root:hover': {
                                backgroundColor: ColorsV2.acent,
                                borderRadius: '8px',
                              },
                              '& .MuiMenuItem-root.Mui-selected:hover': {
                                backgroundColor: ColorsV2.acent,
                                borderRadius: '8px',
                              },
                            },
                          },
                        },
                      }}
                      InputLabelProps={{
                        style: {
                          color: darkmode || errors.firstName ? ColorsV2.white : 'inherit',
                        },
                      }}
                      sx={{
                        margin: '0px 0px 3px 0px',
                        backgroundColor: 'transparent',

                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: ColorsV2.white,
                        },
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: ColorsV2.white,
                          },
                        },

                        '& label.Mui-focused': {
                          color: ColorsV2.white,
                          transform: 'translate(15px, -16px)',
                          fontSize: '0.75rem',
                        },
                        '&  .MuiFormHelperText-root.Mui-error': {
                          background: ColorsV2.secondary,
                          color: '#FF2800',
                          width: '101%',
                          height: '100%',
                          fontSize: '1rem',
                          marginTop: '-0.1%',
                          marginLeft: -0.15,
                          '.MuiSvgIcon-root ': {
                            fill: 'red',
                          },
                        },
                        '.MuiSvgIcon-root ': {
                          fill: 'white',
                        },
                      }}
                      name='role'
                      touched={touched.role}
                      inperror={errors.role}
                      handlechange={handleChange}
                      handleblur={handleBlur}
                    >
                      {roles.map(items => (
                        <MenuItem
                          value={items.id}
                          key={items.id}
                          sx={{
                            margin: '5px',
                          }}
                        >
                          {items.name}
                        </MenuItem>
                      ))}
                    </Input>

                    <Input
                      select
                      fullWidth
                      size='small'
                      val={values.auth}
                      InputProps={{
                        startAdornment: (
                          <CustomAdornment position='start' sx={{ color: ColorsV2.white }}>
                            {t('permission')}:
                          </CustomAdornment>
                        ),
                        style: {
                          color: ColorsV2.white,
                          fontFamily: 'DM Sans, sans-serif',
                          fontSize: '0.7em',
                          fontWeight: '400',
                          lineHeight: '21px',
                          borderRadius: '8px',
                          height: '2.7em',
                        },
                      }}
                      inputProps={{
                        MenuProps: {
                          MenuListProps: {
                            sx: {
                              backgroundColor: ColorsV2.secondary,
                              color: ColorsV2.white,
                              border: `2px solid ${ColorsV2.white}`,
                              '& .MuiMenuItem-root.Mui-selected': {
                                backgroundColor: ColorsV2.acent,
                                borderRadius: '8px',
                              },
                              '& .MuiMenuItem-root:hover': {
                                backgroundColor: ColorsV2.acent,
                                borderRadius: '8px',
                              },
                              '& .MuiMenuItem-root.Mui-selected:hover': {
                                backgroundColor: ColorsV2.acent,
                                borderRadius: '8px',
                              },
                            },
                          },
                        },
                      }}
                      InputLabelProps={{
                        style: {
                          color: darkmode || errors.firstName ? ColorsV2.white : 'inherit',
                        },
                      }}
                      sx={{
                        marginTop: '10px',
                        marginBottom: '40px',
                        backgroundColor: darkmode && 'transparent',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: ColorsV2.white,
                        },
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: ColorsV2.white,
                          },
                        },
                        // backgroundColor: '	#E8E8E8',
                        '& label.Mui-focused': {
                          color: ColorsV2.white,
                          transform: 'translate(15px, -16px)',
                          fontSize: '0.75rem',
                        },
                        '&  .MuiFormHelperText-root.Mui-error': {
                          background: ColorsV2.secondary,
                          color: '#FF2800',
                          width: '101%',
                          height: '100%',
                          fontSize: '1rem',
                          marginTop: '-0.1%',
                          marginLeft: -0.15,
                          '.MuiSvgIcon-root ': {
                            fill: '#FF2800',
                          },
                        },
                        '.MuiSvgIcon-root ': {
                          fill: ColorsV2.white,
                        },
                      }}
                      name='auth'
                      touched={touched.auth}
                      inperror={errors.auth}
                      handlechange={handleChange}
                      handleblur={handleBlur}
                    >
                      {authorities.map(items => (
                        <MenuItem
                          value={items.id}
                          key={items.id}
                          sx={{
                            margin: '5px',
                          }}
                        >
                          {items.name}
                        </MenuItem>
                      ))}
                    </Input>
                    {route.includes('edit-user') ? (
                      <StyledButton
                        name={'IconButton'}
                        onClick={handleDelete}
                        sx={{
                          position: 'absolute',
                          right: '-10px',
                          bottom: '-30px',
                          padding: '10px',
                        }}
                        startIcon={<DeleteIcon fill={ColorsV2.white} fontSize={'0.9em'} />}
                      >
                        {t('delete')}
                      </StyledButton>
                    ) : (
                      ''
                    )}
                  </Box>
                </form>
              </InputContainerStack>
            </ChildGrid>
          </ParentGrid>
        </ParentPaper>
      )}
      {loader && <Loader />}
      <Alert
        open={open}
        title={del ? 'Delete Account' : route.includes('edit-user') ? t('exit_1') : t('save_1')}
        text={
          del
            ? "Are you sure you want to delete this user's account ? "
            : route.includes('edit-user')
            ? t('error_1')
            : t('error_2')
        }
        setOpen={setOpen}
        handleCancel={() => setOpen(false)}
        handleClose={() => setOpen(false)}
        handleConfirmation={del ? handleDeleteConfimation : handleRouteNavigationConfirmation}
      />
      <Toast
        open={toastOpen}
        setOpen={setToastOpen}
        message={state?.message}
        severity={state?.severity}
      />
    </Container>
  );
};

export default Adduser;
